import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ANONYMOUS_NAME } from '@thehubz/shared/model';
import { Subscription, interval } from 'rxjs';
import { ClientEnvironment, ENVIRONMENT } from '../environment';
import { UserSession } from '../models/user-session.model';
import { UserAccessService } from '../user-access/user-access.service';
import { USER_SESSION_TOKEN } from '../user-access/user-session.token';
import { RedirectionUtilityLib } from '../util';
import { StorageUtilityLib } from '../util/storage-utility';

@Injectable({ providedIn: 'root' })
export class CheckSessionService {
  private subscription?: Subscription;

  private _lastActivity: number = Date.now();
  private setActivity = () => {
    this._lastActivity = Date.now();
  };

  private _pageState = this.getPageState();

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private storageUtility: StorageUtilityLib,
    private redirectionUtilityLib: RedirectionUtilityLib,
    private userAccess: UserAccessService,
    private router: Router,
  ) {}

  start(handleLogout?: () => void) {
    if (this.subscription) {
      // already running
      return;
    }

    this._lastActivity = Date.now();
    setTimeout(() => {
      const session = this.storageUtility.getLocalStorageNullable<UserSession>(this.userSessionToken);
      if (session?.userData.username === ANONYMOUS_NAME) {
        // anonymous user, no need to check session
        return;
      }

      if (session) {
        this.checkSession(handleLogout);
      }

      this.observePageState();

      if (this.env.idleTimeout) {
        this.initActivityListener();
      }

      // check runs every minute
      this.subscription = interval(60 * 1000).subscribe(() => {
        this.checkSession(handleLogout);
      });
    }, 20);
  }

  stop() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;

    if (this.env.idleTimeout) {
      this.stopActivityListener();
    }
  }

  defaultLogoutRoutine(navigateToLogin = true) {
    this.storageUtility.clearLocalStorage(this.userSessionToken);
    this.storageUtility.clearAllSessionStorage();
    this.userAccess.clear();
    if (navigateToLogin) {
      this.router.navigate(['/login'], { queryParams: { sessionExpired: true, redirectUrl: document.location.pathname } }).then(() => {
        window.location.reload();
      });
    }
  }

  get hasIdleTimeout(): boolean {
    return this.env.idleTimeout ? Date.now() - this._lastActivity >= this.env.idleTimeout * 60 * 1000 : false;
  }

  trackActivity() {
    this.setActivity();
  }

  private initActivityListener() {
    window.addEventListener('mousemove', this.setActivity);
    window.addEventListener('click', this.setActivity);
    window.addEventListener('keypress', this.setActivity);
    window.addEventListener('DOMMouseScroll', this.setActivity);
    window.addEventListener('mousewheel', this.setActivity);
    window.addEventListener('touchmove', this.setActivity);
  }

  private stopActivityListener() {
    window.removeEventListener('mousemove', this.setActivity);
    window.removeEventListener('click', this.setActivity);
    window.removeEventListener('keypress', this.setActivity);
    window.removeEventListener('DOMMouseScroll', this.setActivity);
    window.removeEventListener('mousewheel', this.setActivity);
    window.removeEventListener('touchmove', this.setActivity);
  }

  isSessionExpired(session: UserSession): boolean {
    return new Date(session.expires_at) < new Date();
  }

  private checkSession(handleLogout?: () => void) {
    const userSession = this.storageUtility.getLocalStorageNullable<UserSession>(this.userSessionToken);
    if (userSession) {
      const timeout = this.hasIdleTimeout;
      if (this.isSessionExpired(userSession) || timeout) {
        console.log(timeout ? 'Idle timeout' : 'Session expired');
        this.redirectionUtilityLib.setRedirectUrl(document.location.pathname);
        if (handleLogout) {
          handleLogout();
        } else {
          this.defaultLogoutRoutine();
        }
        this.stop();
      }
    } else {
      console.log('No user session found');
      // likely logged out already e.g. by the interceptor
      this.stop();

      const urlSearchParams = new URLSearchParams(window.location.search);
      const queryParams = Object.fromEntries(urlSearchParams.entries());
      this.router.navigate(['/login'], { queryParams, replaceUrl: true });
    }
  }

  private getPageState() {
    if (document.visibilityState === 'hidden') {
      return 'hidden';
    }
    if (document.hasFocus()) {
      return 'active';
    }
    return 'passive';
  }

  private observePageState() {
    const logStateChange = (nextState: string) => {
      const prevState = this._pageState;
      if (nextState !== prevState) {
        if (nextState !== 'hidden') {
          this.checkSession();
        }
        this._pageState = nextState;
      }
    };

    ['pageshow', 'focus', 'visibilitychange', 'resume'].forEach((type) => {
      window.addEventListener(type, () => logStateChange(this.getPageState()), { capture: true });
    });
  }
}
